import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignaturePad from '../components/SignaturePad';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { translations, languageFlags, languageNames } from '../lib/translations';
import { FileText, Check, Warning, PencilSimple, Eraser, Download, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestSignPage() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [fesAccepted, setFesAccepted] = useState(false);
  const sigPadRef = useRef(null);

  const [formData, setFormData] = useState({
    full_name: '',
    birth_place: '',
    birth_date: '',
    residence: '',
    document_type_number: '',
    tax_code: ''
  });

  useEffect(() => {
    fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API}/guest/contract/${token}`);
      setContract(response.data);
      if (response.data.guest_data) {
        setFormData(response.data.guest_data);
      }
      if (response.data.status === 'completed') {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Contratto non trovato');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const clearSignature = () => {
    sigPadRef.current?.clear();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields (except tax_code)
    const requiredFields = [
      { key: 'full_name', label: 'Nome completo' },
      { key: 'birth_place', label: 'Luogo di nascita' },
      { key: 'birth_date', label: 'Data di nascita' },
      { key: 'residence', label: 'Residenza' },
      { key: 'document_type_number', label: 'Documento' }
    ];
    for (const field of requiredFields) {
      if (!formData[field.key] || !formData[field.key].trim()) {
        toast.error(`${field.label} è obbligatorio`);
        return;
      }
    }

    if (sigPadRef.current?.isEmpty()) {
      toast.error('Firma richiesta');
      return;
    }

    setSubmitting(true);
    const signature = sigPadRef.current.toDataURL();

    try {
      await axios.post(`${API}/guest/contract/${token}/sign`, {
        ...formData,
        signature,
        user_agent: navigator.userAgent
      });
      setSubmitted(true);
      toast.success('Contratto firmato con successo!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella firma');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <p className="text-zinc-500">Caricamento...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <Warning size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-zinc-900 mb-2">Errore</h1>
          <p className="text-zinc-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!contract) return null;

  const t = translations[contract.language];

  if (submitted) {
    const downloadPdf = async () => {
      try {
        const response = await axios.get(`${API}/guest/contract/${token}/pdf`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `contratto_firmato.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast.error('Errore nel download');
      }
    };

    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 sm:p-6">
        <div className="max-w-md w-full bg-white border border-zinc-200 p-6 sm:p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} weight="bold" className="text-green-600" />
          </div>
          <h1 className="text-2xl font-black text-zinc-900 mb-2">{t.contractSigned}</h1>
          <p className="text-zinc-500 mb-4">
            {contract.language === 'ITA' && 'Il contratto è stato firmato correttamente. Il proprietario riceverà una notifica.'}
            {contract.language === 'ENG' && 'The contract has been signed successfully. The owner will receive a notification.'}
            {contract.language === 'FRA' && 'Le contrat a été signé avec succès. Le propriétaire recevra une notification.'}
            {contract.language === 'SPA' && 'El contrato ha sido firmado exitosamente. El propietario recibirá una notificación.'}
            {contract.language === 'TED' && 'Der Vertrag wurde erfolgreich unterschrieben. Der Eigentümer erhält eine Benachrichtigung.'}
          </p>
          <div className="bg-teal-50 border border-teal-200 p-3 mb-6 text-xs text-teal-800">
            {contract.language === 'ITA' && 'Scarica il PDF per i tuoi archivi. Il documento resterà disponibile fino alla fine del mese solare.'}
            {contract.language === 'ENG' && 'Download the PDF for your records. The document will be available until the end of the calendar month.'}
            {contract.language === 'FRA' && 'Téléchargez le PDF pour vos archives. Le document sera disponible jusqu\'à la fin du mois calendaire.'}
            {contract.language === 'SPA' && 'Descarga el PDF para tus archivos. El documento estará disponible hasta fin del mes calendario.'}
            {contract.language === 'TED' && 'Laden Sie das PDF für Ihre Unterlagen herunter. Das Dokument ist bis zum Ende des Kalendermonats verfügbar.'}
          </div>
          <Button
            onClick={downloadPdf}
            className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-5"
            data-testid="download-signed-pdf-button"
          >
            <Download size={20} className="mr-2" />
            {t.download} PDF
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center">
              <FileText size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-black text-zinc-900 tracking-tight">
                {contract.language === 'ITA' && 'CONTRATTO DI LOCAZIONE TURISTICA'}
                {contract.language === 'ENG' && 'TOURIST LEASE AGREEMENT'}
                {contract.language === 'FRA' && 'CONTRAT DE LOCATION TOURISTIQUE'}
                {contract.language === 'SPA' && 'CONTRATO DE ARRENDAMIENTO TURISTICO'}
                {contract.language === 'TED' && 'TOURISTISCHER MIETVERTRAG'}
              </h1>
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <span>{languageFlags[contract.language]}</span>
                <span>{languageNames[contract.language]}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Contract Summary */}
        <div className="bg-white border border-zinc-200 p-4 sm:p-6 mb-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">{t.contractDetails}</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 text-sm">
            <div>
              <h3 className="font-bold text-zinc-900 mb-2">{t.landlord}</h3>
              <p className="text-zinc-600">{contract.owner_data.full_name}</p>
            </div>
            <div>
              <h3 className="font-bold text-zinc-900 mb-2">{t.propertyObject}</h3>
              <p className="text-zinc-600">
                {contract.owner_data.property_address}, {contract.owner_data.property_number}<br />
                {contract.owner_data.property_city} ({contract.owner_data.property_province})
              </p>
            </div>
            <div>
              <h3 className="font-bold text-zinc-900 mb-2">{t.stayDuration}</h3>
              <p className="text-zinc-600">
                {contract.rental_data.nights} {t.nights}<br />
                {t.checkIn}: {contract.rental_data.check_in_date} - {contract.rental_data.check_in_time}<br />
                {t.checkOut}: {contract.rental_data.check_out_date} - {contract.rental_data.check_out_time}
              </p>
            </div>
            <div>
              <h3 className="font-bold text-zinc-900 mb-2">{t.rentPayment}</h3>
              <p className="text-zinc-600">
                {t.totalRent}: €{contract.rental_data.total_rent}<br />
                {t.touristTax}: €{contract.rental_data.tourist_tax}<br />
                {t.paymentMethod}: {contract.rental_data.payment_method}
              </p>
            </div>
          </div>

          {/* Owner Signature */}
          {contract.owner_signature && (
            <div className="mt-6 pt-6 border-t border-zinc-200">
              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-2">{t.landlordSignature}</h3>
              <div className="border border-zinc-200 bg-zinc-50 p-4 inline-block">
                <img src={contract.owner_signature} alt="Firma proprietario" className="max-h-16" />
              </div>
            </div>
          )}
        </div>

        {/* Guest Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white border border-zinc-200 p-4 sm:p-6 mb-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-6">{t.guestData}</h2>
            
            <div className="grid gap-5">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.fullName}</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  required
                  className="rounded-none mt-2"
                  data-testid="guest-fullname-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.birthPlace}</Label>
                  <Input
                    value={formData.birth_place}
                    onChange={(e) => handleChange('birth_place', e.target.value)}
                    required
                    className="rounded-none mt-2"
                    data-testid="guest-birthplace-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.birthDate}</Label>
                  <Input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => handleChange('birth_date', e.target.value)}
                    required
                    className="rounded-none mt-2"
                    data-testid="guest-birthdate-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.residence}</Label>
                <Input
                  value={formData.residence}
                  onChange={(e) => handleChange('residence', e.target.value)}
                  required
                  className="rounded-none mt-2"
                  data-testid="guest-residence-input"
                />
              </div>

              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.documentNumber}</Label>
                <Input
                  value={formData.document_type_number}
                  onChange={(e) => handleChange('document_type_number', e.target.value)}
                  placeholder="es. Passport AB123456"
                  required
                  className="rounded-none mt-2"
                  data-testid="guest-document-input"
                />
              </div>

              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.taxCode} (opzionale)</Label>
                <Input
                  value={formData.tax_code}
                  onChange={(e) => handleChange('tax_code', e.target.value.toUpperCase())}
                  className="rounded-none mt-2"
                  data-testid="guest-taxcode-input"
                />
              </div>
            </div>
          </div>

          {/* Signature */}
          <div className="bg-white border border-zinc-200 p-4 sm:p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <PencilSimple size={24} weight="bold" className="text-zinc-700" />
              <h2 className="text-lg font-bold text-zinc-900">{t.signature}</h2>
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              {contract.language === 'ITA' && 'Firma con il dito o il mouse nello spazio sottostante'}
              {contract.language === 'ENG' && 'Sign with your finger or mouse in the space below'}
              {contract.language === 'FRA' && 'Signez avec votre doigt ou votre souris dans l\'espace ci-dessous'}
              {contract.language === 'SPA' && 'Firme con el dedo o el ratón en el espacio de abajo'}
              {contract.language === 'TED' && 'Unterschreiben Sie mit dem Finger oder der Maus im Feld unten'}
            </p>

            <div className="border border-zinc-300 bg-zinc-50 mb-4">
              <SignaturePad
                ref={sigPadRef}
                height={220}
                data-testid="guest-signature-pad"
              />
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={clearSignature}
              className="rounded-none border-zinc-300"
              data-testid="clear-guest-signature-button"
            >
              <Eraser size={18} className="mr-2" />
              {t.clearSignature}
            </Button>
          </div>

          {/* FES Legal Disclosure */}
          <div className="bg-amber-50 border border-amber-200 p-4 sm:p-6 mb-4">
            <p className="text-xs text-amber-900 leading-relaxed mb-4 font-medium">
              {contract.language === 'ITA' && 'INFORMATIVA FIRMA ELETTRONICA SEMPLICE (FES) — Art. 20 D.Lgs. 82/2005 (CAD), Reg. UE 910/2014 (eIDAS)'}
              {contract.language === 'ENG' && 'SIMPLE ELECTRONIC SIGNATURE (SES) NOTICE — Art. 20 D.Lgs. 82/2005 (CAD), EU Reg. 910/2014 (eIDAS)'}
              {contract.language === 'FRA' && 'AVIS SIGNATURE ÉLECTRONIQUE SIMPLE (SES) — Art. 20 D.Lgs. 82/2005 (CAD), Rég. UE 910/2014 (eIDAS)'}
              {contract.language === 'SPA' && 'AVISO FIRMA ELECTRÓNICA SIMPLE (FES) — Art. 20 D.Lgs. 82/2005 (CAD), Reg. UE 910/2014 (eIDAS)'}
              {contract.language === 'TED' && 'HINWEIS EINFACHE ELEKTRONISCHE SIGNATUR (EES) — Art. 20 D.Lgs. 82/2005 (CAD), EU-Verordnung 910/2014 (eIDAS)'}
            </p>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="fes-consent-label">
              <input
                type="checkbox"
                checked={fesAccepted}
                onChange={(e) => setFesAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 border-amber-400 rounded-none accent-zinc-900 flex-shrink-0"
                data-testid="fes-consent-checkbox"
              />
              <span className="text-xs text-zinc-800 leading-relaxed">
                {contract.language === 'ITA' && 'Dichiaro di voler sottoscrivere il presente documento e sono consapevole che, ai fini della validità legale (ex art. 20 CAD), verranno registrati i dati tecnici della connessione (indirizzo IP e Device ID). Tali dati saranno incorporati esclusivamente nel PDF del contratto e non saranno conservati sui server della piattaforma.'}
                {contract.language === 'ENG' && 'I declare my intention to sign this document and acknowledge that, for legal validity purposes (Art. 20 CAD), the technical connection data (IP address and Device ID) will be recorded. Such data will be embedded exclusively in the contract PDF and will not be stored on the platform servers.'}
                {contract.language === 'FRA' && 'Je déclare vouloir signer ce document et je suis conscient que, aux fins de la validité juridique (art. 20 CAD), les données techniques de connexion (adresse IP et Device ID) seront enregistrées. Ces données seront intégrées exclusivement dans le PDF du contrat et ne seront pas conservées sur les serveurs de la plateforme.'}
                {contract.language === 'SPA' && 'Declaro mi intención de firmar este documento y soy consciente de que, a efectos de validez legal (art. 20 CAD), se registrarán los datos técnicos de conexión (dirección IP y Device ID). Dichos datos se incorporarán exclusivamente en el PDF del contrato y no se conservarán en los servidores de la plataforma.'}
                {contract.language === 'TED' && 'Ich erkläre, dass ich dieses Dokument unterzeichnen möchte, und bin mir bewusst, dass zu Zwecken der Rechtsgültigkeit (Art. 20 CAD) die technischen Verbindungsdaten (IP-Adresse und Geräte-ID) erfasst werden. Diese Daten werden ausschließlich in das Vertrags-PDF eingebettet und nicht auf den Servern der Plattform gespeichert.'}
                {' *'}
              </span>
            </label>
          </div>

          {/* Privacy Consent */}
          <div className="bg-zinc-50 border border-zinc-200 p-4 sm:p-6 mb-6">
            <p className="text-xs text-zinc-600 leading-relaxed mb-4">
              {t.privacyConsent}
            </p>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="privacy-consent-label">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 border-zinc-300 rounded-none accent-zinc-900 flex-shrink-0"
                data-testid="privacy-consent-checkbox"
              />
              <span className="text-sm font-medium text-zinc-900">{t.privacyAccept} *</span>
            </label>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting || !privacyAccepted || !fesAccepted}
            className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-6 disabled:opacity-40"
            data-testid="submit-guest-signature-button"
          >
            {submitting ? '...' : t.submitSignature}
          </Button>
        </form>
      </main>
    </div>
  );
}
